/**
 * fire_oracle.ts — the CPU MIRROR of the fire shaders (fire realism R1,
 * 2026-09-25). Pure: no three. `verify:fire` marches it to pin what the GPU
 * march must produce — the core's radiance, the soot's occlusion, the colour
 * by height — and every shader constant lives HERE, once, so the TSL graph
 * (lib/vfx/fire_volume.ts) and its oracle can never drift apart silently.
 *
 * THE FLAME MODEL (fire realism, 2026-10-08 — rebuilt against 97 measured photographs; the record and the numbers are
 * in docs/research/2026-10-08-fire-realism). What the first model got wrong, and what this one does instead:
 *
 *   · IT WAS A CONE OF SPECKLE. Its density was the 64³ volume's FINE channel (two octaves, 2–5 cm features) thresholded
 *     inside a taper: a narrow cone of grain. A flame is a few TONGUES: the structure noise is the volume's four-octave
 *     channel, stretched along the rise, against a threshold that climbs with height — whole at the base, separate
 *     tongues above it, detached flamelets at the top — inside an ENVELOPE with a belly (a pinched base for a torch).
 *   · IT WAS A SOLID. Temperature followed density, so every thin part was also cold: dark red grain round a hot blob.
 *     A flame is thin SHEETS: density peaks in a shell at the threshold and falls inside it (`hollow`), temperature is a
 *     field of its own (hot low on the axis, cooler up and out), and the thick lower middle is the bright CORE.
 *   · IT SCROLLED AT ONE SPEED. The noise rose at a constant 1.4 m/s. A buoyant flame ACCELERATES (McCaffrey's
 *     continuous-flame law, u ∝ √z): the rising coordinate is √(height), so a feature climbs at 3.2·√z m/s and
 *     stretches as it goes; a PUFF (a bulge of the envelope) travels up at the fire's own shedding frequency
 *     (fire_rules `firePuffHz`, 1.5 / √D Hz) and pinches the tongues off above it.
 *   · ITS BOX SHOWED. Density now fades to nothing 7 % inside every wall and the lid (a leaning plume was cut flat).
 */
import { sampleLut } from './blackbody.js';
import { NOISE3D_SIZE, sampleNoise3D } from './noise3d.js';

/** The shader constants the CPU oracle mirrors (keep them in ONE place). */
export const FIRE_SHADER = {
  /** Emission scale per metre of flame sheet at density 1 (× the per-fire exposure) — the core is `1 + coreK` of it. */
  emissionPerM: 30,
  /** The flame body's extinction (1/m at density 1): a thick core hides about half of what is behind it. */
  sigmaFlame: 1.0,
  /** Cool soot's extinction (1/m at density 1). */
  sigmaSoot: 7.5,
  /** Soot's single-scattering albedo toward the smoke light. */
  sootAlbedo: 0.55,
  /** A campfire's envelope: base radius (box-local units; the box is ±0.5) and height (share of the box). Per kind: fire_rules FIRE_SHAPE. */
  baseRadius: 0.34,
  flameHeight: 0.60,
  /** THE RISING COORDINATE: rise = √(metres·2.3 + riseY0)·riseK − t·riseW (box heights are 2.3 m at size 1). */
  riseK: 0.62, riseW: 1.0, riseY0: 0.05,
  /** The envelope's closing top: (1 − h^envE)^envG. */
  envE: 1.6, envG: 0.6,
  /**
   * The tongue threshold at the base and at the tip, the sheet's softness, the fine channel's share. `soft` 0.42 and
   * `fine` 0: a 12–16 step march samples every 8–10 cm, so a sheet thinner than that (soft 0.26) or the fine channel's
   * 2–5 cm grain is ALIASED along the ray and the per-pixel start jitter prints it as a checkerboard on the flame.
   */
  thr0: -0.60, thr1: 0.50, thrTongue: 0.6, soft: 0.5, fine: 0.0,
  /** Sheets: emission ∝ density^densPow; the interior of a sheet keeps (1 − hollow) of its density. */
  densPow: 1.3, hollow: 0.45,
  /** The core (the thick lower middle) burns this much brighter than the body. */
  coreK: 1.4,
  /** The flame's light on the soot above it, against the flame's own emission (≈ 1 % of the core: measured halo). */
  underGlow: 0.15,
  /** The walls' and the lid's fade (box-local units): nothing reaches a face of the box. */
  wallFade: 0.07, lidFade: 0.08,
  /**
   * Per-rung gains so every rung's core matches the volume's (`verify:fire-realism-bed:probe` measures the rungs against
   * each other): a card is ONE sample of the same field × a slab of flame `cardSlab` of the box wide; the sim's grid.
   */
  cardSlab: 0.42,
  cardGain: 0.6,
  /** A card is ONE plane through the flame: its tongue threshold is lowered by this, or the slice shows a sliver. */
  cardThrBias: -0.3,
  simGain: 3.3,
};

/** The per-fire shape vectors a material falls back to when its mesh carries none (a campfire): fireC, fireD. */
export const FIRE_DEFAULT_C: readonly [number, number, number, number] = [0, FIRE_SHADER.baseRadius, 0, 0.14];
export const FIRE_DEFAULT_D: readonly [number, number, number, number] = [1.2, 0.7, 0.3, 1.0];

// ── The CPU ORACLE (verify:fire): the same march on the CPU ─────────────────
const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const sstep = (a: number, b: number, x: number): number => { const t = clamp01((x - a) / (b - a)); return t * t * (3 - 2 * t); };
type V4 = readonly [number, number, number, number];
/**
 * The flame's emission + extinction at a box-local point — a line-for-line mirror of `flameSample`.
 * A = (seed, exposure, heat, t) · B = (leanX, leanZ, flame height, soot) · C = (card yaw, base radius, puff phase,
 * puff amplitude) · D = (tongue frequency, raggedness, pinch, sway).
 */
export function flameSampleCpu(noise: Uint8Array, lut: Float32Array, p: readonly [number, number, number], A: V4, B: V4, smoke: readonly [number, number, number] = [0.18, 0.2, 0.24], C: V4 = FIRE_DEFAULT_C, D: V4 = FIRE_DEFAULT_D, thrBias = 0): [number, number, number, number] {
  const S = NOISE3D_SIZE, F = FIRE_SHADER;
  const [seed, exposure, heat, t] = A;
  const hF = Math.max(B[2], 0.2);
  const y01 = p[1] + 0.5;
  const h = y01 / hF, hc = clamp01(h);
  const rise = Math.sqrt(Math.max(y01 * 2.3, 0) + F.riseY0) * F.riseK - t * F.riseW;
  // fetch 1: the sway
  const wv = sampleNoise3D(noise, S, p[0] * 0.7 + seed, rise * 0.45 + t * 0.09 + seed * 0.61, p[2] * 0.7 + seed * 0.37);
  const sw = (0.035 + 0.115 * hc) * D[3];
  const qx = p[0] + (wv[0] - 0.5) * 2 * sw, qz = p[2] + (wv[2] - 0.5) * 2 * sw;
  // the envelope, the puff
  const b0 = 1 - 0.65 * D[2], hb = 0.18 + 0.4 * D[2];
  const env = (b0 + (1 - b0) * sstep(0, hb, hc)) * Math.max(1 - hc ** F.envE, 0) ** F.envG;
  const puff = 1 + C[3] * Math.sin((C[2] - h * 0.9) * 6.2831853);
  const R = C[1] * env * puff;
  const lx = B[0] * h * h, lz = B[1] * h * h;
  const rr = Math.hypot(qx - lx, qz - lz);
  const r = rr / Math.max(R, 0.012);
  const body = 1 - sstep(0.55, 1.0, r);
  // fetch 2: the tongues
  const n4 = sampleNoise3D(noise, S, qx * 1.3 * D[0] + seed * 0.37 + t * 0.031, rise + seed * 0.23, qz * 1.3 * D[0] + seed * 0.71 - t * 0.023);
  const n = (n4[0] - 0.5) * 2 + (n4[3] - 0.5) * 2 * F.fine;
  const n2 = (n4[1] - 0.5) * 2;
  // FEWER TONGUES = A MORE SOLID FLAME: a torch's (0.8) holds together to its tip, a wreck's (1.35) breaks up sooner.
  const thr = F.thr0 + (F.thr1 + (D[0] - 1.2) * F.thrTongue - F.thr0) * hc ** 1.1 + D[1] * 0.5 * r * r + thrBias * (1 - 0.7 * hc);
  const x = n - thr;
  const shell = sstep(0, F.soft, x), inner = sstep(F.soft, F.soft + 0.5, x);
  const wall = clamp01((0.5 - Math.abs(p[0])) / F.wallFade) * clamp01((0.5 - Math.abs(p[2])) / F.wallFade) * clamp01((0.5 - p[1]) / F.lidFade);
  const dens = shell * (1 - F.hollow * inner) * body * (1 - sstep(1.0, 1.12, h)) * wall;
  const rc = Math.min(r, 1.2);
  const temp = clamp01(heat * (1 - 0.26 * hc ** 1.3 - 0.22 * rc * rc + 0.07 * n2) * (0.70 + 0.30 * shell * (1 - 0.7 * inner)));
  const bb = sampleLut(lut, temp);
  const core = (1 - sstep(0, 0.6, r)) * (1 - sstep(0.1, 0.75, hc));
  const k = bb[3] * dens ** F.densPow * (1 + F.coreK * core) * F.emissionPerM * exposure;
  // soot: the dark breath at the tips (a short plume: the smoke COLUMN is the particles' / the smoke cards')
  const cool = 1 - sstep(0.3, 0.6, temp);
  const plume = sstep(hF * 0.82, hF * 1.1, y01) * (1 - sstep(hF * 1.25, hF * 1.75, y01));
  const plumeR = 0.10 + Math.max(0, y01 - hF * 0.8) * 0.34;
  const plumeShape = 1 - sstep(0.4, 1.0, rr / plumeR);
  const sootDens = clamp01(plume * plumeShape * (n * 0.9 + 0.45)) * B[3] * wall;
  const sigma = dens * (F.sigmaFlame + cool * F.sigmaSoot * 0.12) + sootDens * F.sigmaSoot * 0.5;
  const under = Math.exp(-Math.max(0, y01 - hF) * 4.5) * heat * F.underGlow;
  const lit = [smoke[0] + 1.0 * under, smoke[1] + 0.42 * under, smoke[2] + 0.12 * under];
  const sc = (sootDens + dens * cool * 0.12) * F.sigmaSoot * 0.5 * F.sootAlbedo;
  return [bb[0] * k + lit[0] * sc, bb[1] * k + lit[1] * sc, bb[2] * k + lit[2] * sc, sigma];
}

/**
 * March one ray through a fire box on the CPU (box-local origin + direction
 * per WORLD metre, like the shader) — returns premultiplied rgb and α.
 */
export function marchFireCpu(noise: Uint8Array, lut: Float32Array, o: [number, number, number], d: [number, number, number], steps: number, A: V4, B: V4, tOpaque = Infinity, C: V4 = FIRE_DEFAULT_C, D: V4 = FIRE_DEFAULT_D, jit = 0.5): [number, number, number, number] {
  const inv = d.map((v) => 1 / (v + 1e-7));
  let tEnter = 0, tExit = Infinity;
  for (let i = 0; i < 3; i++) {
    const a = (-0.5 - o[i]) * inv[i], b = (0.5 - o[i]) * inv[i];
    tEnter = Math.max(tEnter, Math.min(a, b)); tExit = Math.min(tExit, Math.max(a, b));
  }
  const tEnd = Math.min(tExit, tOpaque);
  const Cc = [0, 0, 0];
  let T = 1;
  if (tEnd > tEnter) {
    const ds = (tEnd - tEnter) / steps;
    let t = tEnter + ds * jit;
    for (let i = 0; i < steps && T > 0.015 && t < tEnd; i++) {
      const p: [number, number, number] = [o[0] + d[0] * t, o[1] + d[1] * t, o[2] + d[2] * t];
      const s = flameSampleCpu(noise, lut, p, A, B, undefined, C, D);
      const att = Math.exp(-s[3] * ds);
      const w = s[3] > 1e-4 ? (1 - att) / s[3] : ds;
      Cc[0] += s[0] * T * w; Cc[1] += s[1] * T * w; Cc[2] += s[2] * T * w;
      T *= att;
      t += ds;
    }
  }
  return [Cc[0], Cc[1], Cc[2], 1 - T];
}

/**
 * THE CORE RAY (fire_rules FIRE_CORE_REF's definition): a horizontal ray through the axis of a campfire's box at 0.3 of
 * its flame's height — the thick lower middle, the part of a flame that is always there — at exposure 1, heat 1.
 * `phase` is the puff's (the harness sweeps it with the time).
 */
export function coreRayCpu(noise: Uint8Array, lut: Float32Array, seed: number, t: number, phase = 0, steps = 16): [number, number, number, number] {
  const W = 1.3;
  const y = 0.3 * FIRE_SHADER.flameHeight - 0.5;
  return marchFireCpu(noise, lut, [0, y, -5 / W], [0, 0, 1 / W], steps, [seed, 1, 1, t], [0, 0, FIRE_SHADER.flameHeight, 0.5], Infinity, [0, FIRE_DEFAULT_C[1], phase, FIRE_DEFAULT_C[3]], FIRE_DEFAULT_D);
}

/**
 * THE LIGHT FOLLOWS THE FLAME (fire realism, 2026-10-08): the flame's emission as six short rays see it — two sides,
 * three heights — at exposure 1. The fire system divides it by its own running mean and drives the fire's point light
 * with the ratio, so the light on the ground swells when a tongue stands up and dips when the flame breaks (the light
 * used to follow the flicker's number alone: its correlation with the drawn flame fell to 0.1–0.2 once the flame had
 * real structure). ~70 noise fetches a lit fire a frame.
 */
export function flameEmissionCpu(noise: Uint8Array, lut: Float32Array, A: V4, B: V4, C: V4 = FIRE_DEFAULT_C, D: V4 = FIRE_DEFAULT_D): number {
  const hF = Math.max(B[2], 0.2);
  const a: V4 = [A[0], 1, A[2], A[3]], b: V4 = [B[0], B[1], B[2], 0];
  let sum = 0;
  for (const h of [0.15, 0.4, 0.65]) {
    const y = h * hF - 0.5;
    const c1 = marchFireCpu(noise, lut, [0, y, -1], [0, 0, 1], 6, a, b, Infinity, C, D);
    const c2 = marchFireCpu(noise, lut, [-1, y, 0], [1, 0, 0], 6, a, b, Infinity, C, D);
    sum += 0.2126 * (c1[0] + c2[0]) + 0.7152 * (c1[1] + c2[1]) + 0.0722 * (c1[2] + c2[2]);
  }
  return sum / 6;
}

// ── The sprite rung's flipbook (fire_volume bakes it; verify:fire pins its loop) ──
/** See fire_volume `stepFireFlipbook`: frames × (w × h) texels of the campfire's flame, front on. */
export const FIRE_FLIP = { frames: 8, w: 40, h: 64, fps: 12, spanX: 0.84, spanY: 0.8, steps: 8, ref: 4.0, layerGain: 0.62, t0: 3.1, seed: 0.31 } as const;
/** One texel of frame k: the flame's premultiplied radiance through the box, front on (shader units). */
export function flipTexelCpu(noise: Uint8Array, lut: Float32Array, k: number, ix: number, iy: number): [number, number, number] {
  const Fp = FIRE_FLIP, W = 1.3;
  const x = ((ix + 0.5) / Fp.w - 0.5) * Fp.spanX, y = (iy + 0.5) / Fp.h * Fp.spanY - 0.5;
  const T = Fp.frames / Fp.fps, t = Fp.t0 + k / Fp.fps, a = k / Fp.frames;
  // soot 0: the sprite is ADDITIVE — the plume's grey in-scatter would glow as a box over the flame (the smoke is the particles')
  const B: [number, number, number, number] = [0, 0, FIRE_SHADER.flameHeight, 0];
  const C: [number, number, number, number] = [0, FIRE_DEFAULT_C[1], k / Fp.frames, FIRE_DEFAULT_C[3]];
  const m1 = marchFireCpu(noise, lut, [x, y, -5 / W], [0, 0, 1 / W], Fp.steps, [Fp.seed, 1, 1, t], B, Infinity, C, FIRE_DEFAULT_D);
  if (a <= 0) return [m1[0], m1[1], m1[2]];
  const m2 = marchFireCpu(noise, lut, [x, y, -5 / W], [0, 0, 1 / W], Fp.steps, [Fp.seed, 1, 1, t - T], B, Infinity, C, FIRE_DEFAULT_D);
  return [m1[0] * (1 - a) + m2[0] * a, m1[1] * (1 - a) + m2[1] * a, m1[2] * (1 - a) + m2[2] * a];
}
